import React, { useState } from 'react';

import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Flex,
  Modal,
  Paper,
  ScrollArea,
  Space,
  Text,
  TextInput,
  Title
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconInfoCircle, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError, HttpStatusCode } from 'axios';
import { ErrorAlert } from 'components/ErrorAlert';
import { FullScreenLoader } from 'components/FullScreenLoader';
import { NoDataFound } from 'components/NoDataFound';
import { t } from 'i18next';
import { CaldotcomIcon } from 'icons/Caldotcom';
import { CaldotcomService } from 'services/caldotcom-service';
import { NotificationService } from 'services/notification-service';
import { CALDOTCOM_INTEGRATION_GUIDE_URL } from 'shared/constants/caldotcom';
import { CALDOTCOM_ACCOUNTS_QUERY_KEY } from 'shared/constants/query-keys';
import { CaldotcomAccount } from 'shared/interfaces/Caldotcom';
import { requiredInputValidator } from 'shared/utils/form-validators';

type CalDotcomAccountHandlerProps = BoxProps;

export function CalDotcomAccountHandler({ ...boxProps }: CalDotcomAccountHandlerProps) {
  const queryClient = useQueryClient();

  const [caldotcomConnectAccountModalOpen, setCaldotcomConnectAccountModalOpen] = useState(false);

  const caldotcomAccountsQuery = useQuery({
    queryKey: [CALDOTCOM_ACCOUNTS_QUERY_KEY],
    queryFn: () => CaldotcomService.getConnectedAccounts()
  });

  const connectCalDotcomAccountMutation = useMutation({
    mutationFn: (caldotcomApiKey: string) => CaldotcomService.connectAccount(caldotcomApiKey),
    onSuccess: (caldotcomAccount: CaldotcomAccount) => {
      closeCaldotcomConnectAccountModal();
      NotificationService.showResult(true, 'FEED_BACK.CALDOTCOM.SUCCESS.ADD_ACCOUNT');
      queryClient.setQueryData([CALDOTCOM_ACCOUNTS_QUERY_KEY], (prevData: CaldotcomAccount[] | undefined) => [
        ...(prevData || []),
        caldotcomAccount
      ]);
    },
    onError: () => {
      NotificationService.showResult(false, 'FEED_BACK.CALDOTCOM.FAILURE.ADD_ACCOUNT');
    }
  });

  const deleteCalDotcomAccountMutation = useMutation({
    mutationFn: (caldotcomAccountId: string) => CaldotcomService.deleteConnectedAccount(caldotcomAccountId),
    onSuccess: (_responseData, caldotcomAccountId) => {
      closeCaldotcomConnectAccountModal();
      NotificationService.showResult(true, 'FEED_BACK.CALDOTCOM.SUCCESS.DELETE_ACCOUNT');
      queryClient.setQueryData([CALDOTCOM_ACCOUNTS_QUERY_KEY], (prevData: CaldotcomAccount[] | undefined) =>
        prevData?.filter((account) => account._id !== caldotcomAccountId)
      );
    },
    onError: (error: AxiosError) => {
      const errorCode = error.response?.status;
      if (errorCode === HttpStatusCode.Conflict) {
        NotificationService.showResult(undefined, 'FEED_BACK.CALDOTCOM.FAILURE.DELETE_ACCOUNT_CONFLICT');
      } else {
        NotificationService.showResult(false, 'FEED_BACK.CALDOTCOM.FAILURE.DELETE_ACCOUNT');
      }
    }
  });

  const connectCaldotcomAccountForm = useForm({
    initialValues: { caldotcomApiKey: '' },
    validate: { caldotcomApiKey: (value) => requiredInputValidator(value) }
  });

  const closeCaldotcomConnectAccountModal = () => {
    setCaldotcomConnectAccountModalOpen(false);
    connectCaldotcomAccountForm.reset();
  };
  const openCaldotcomConnectAccountModal = () => setCaldotcomConnectAccountModalOpen(true);

  const handleConnectCalDotcomAccount = () => {
    const validation = connectCaldotcomAccountForm.validate();
    if (validation.hasErrors) return;
    connectCalDotcomAccountMutation.mutate(connectCaldotcomAccountForm.values.caldotcomApiKey);
  };

  const ConnectAccountButton = ({ ...buttonProps }: ButtonProps) => (
    <Button
      variant="default"
      onClick={openCaldotcomConnectAccountModal}
      leftIcon={<CaldotcomIcon h={18} w={18} />}
      {...buttonProps}>
      {t('CALDOTCOM.CONNECT_CALDOTCOM_ACCOUNT')}
    </Button>
  );

  if (caldotcomAccountsQuery.isError) return <ErrorAlert h={boxProps.h} />;
  if (caldotcomAccountsQuery.isInitialLoading) return <FullScreenLoader h={boxProps.h} />;
  const caldotcomAccounts = caldotcomAccountsQuery.data || [];

  return (
    <>
      <Box {...boxProps}>
        <Flex justify="space-between" align="center">
          <Flex direction="row" align="center">
            <Title order={5}>{t('CALDOTCOM.CONNECTED_ACCOUNT')}</Title>
            <Space w={10} />
            <Badge px={4} mt={3} size="xs" color="gray">
              {caldotcomAccounts.length}
            </Badge>
          </Flex>
          {caldotcomAccounts.length > 0 && <ConnectAccountButton variant="default" />}
        </Flex>
        <ScrollArea w="100%" h="100%" mt={10}>
          {caldotcomAccounts.map((account, idx) => (
            <Paper
              key={idx}
              p={5}
              mt={idx === 0 ? 0 : 5}
              mb={idx === caldotcomAccounts.length - 1 ? 0 : 5}
              px={10}
              withBorder>
              <Flex direction="row" justify="space-between" align="center" w="100%" h="100%">
                <Flex direction="row" align="center">
                  <Text>{account.accountInfo.email}</Text>
                </Flex>

                <ActionIcon
                  color="red"
                  onClick={(event) => {
                    event.stopPropagation();
                    deleteCalDotcomAccountMutation.mutate(account._id);
                  }}>
                  <IconTrash size={22} />
                </ActionIcon>
              </Flex>
            </Paper>
          ))}
          {caldotcomAccounts.length == 0 && (
            <NoDataFound
              h="100%"
              size="xs"
              icon={<CaldotcomIcon />}
              title={t('NO_DATA_FOUND.CALDOTCOM.TITLE')}
              subTitle={t('NO_DATA_FOUND.CALDOTCOM.SUB_TITLE')}
              button={<ConnectAccountButton size="xs" />}
            />
          )}
        </ScrollArea>
      </Box>
      <Modal
        opened={caldotcomConnectAccountModalOpen}
        onClose={closeCaldotcomConnectAccountModal}
        size={600}
        title={t('CALDOTCOM.CONNECT_CALDOTCOM_ACCOUNT_MODAL.TITLE')}>
        <Alert color="blue" icon={<IconInfoCircle />}>
          <Text size="sm" weight={500}>
            {t('CALDOTCOM.GUIDE.TITLE')}
          </Text>
          <Text size="xs">
            {t('CALDOTCOM.GUIDE.TEXT')}{' '}
            <Anchor href={CALDOTCOM_INTEGRATION_GUIDE_URL} target="_blank" rel="noopener noreferrer" inherit>
              {t('CALDOTCOM.GUIDE.LINK_TEXT')}
            </Anchor>
          </Text>
        </Alert>

        <Space h={10} />

        <TextInput
          placeholder={t('CALDOTCOM.CONNECT_CALDOTCOM_ACCOUNT_MODAL.API_KEY_PLACEHOLDER')}
          {...connectCaldotcomAccountForm.getInputProps('caldotcomApiKey')}
        />

        <Space h={10} />
        <Flex direction="row" justify="flex-end">
          <Button loading={connectCalDotcomAccountMutation.isLoading} onClick={handleConnectCalDotcomAccount}>
            {t('CALDOTCOM.CONNECT_CALDOTCOM_ACCOUNT_MODAL.CONNECT_BUTTON')}
          </Button>
        </Flex>
      </Modal>
    </>
  );
}
